import type {
  CustomerProductPurchase,
  CustomerProductPurchasesListResponse,
} from '../models/customerProductPurchase.model';

function toOptionalNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function toBoolean(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'yes' || s === '1') return true;
    if (s === 'false' || s === 'no' || s === '0') return false;
  }
  if (typeof v === 'number') return v !== 0;
  return false;
}

function firstString(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

export function mapCustomerProductPurchaseFromApi(
  raw: Record<string, unknown>
): CustomerProductPurchase {
  return {
    Id: firstString(raw.Id, raw.id, raw._id),
    CustomerId: firstString(raw.customerId, raw.CustomerId),
    TenantId: firstString(raw.tenantId, raw.TenantId),
    StripeInvoiceId: firstString(raw.stripeInvoiceId, raw.StripeInvoiceId),
    StripeLineItemId: firstString(raw.stripeLineItemId, raw.StripeLineItemId),
    StripeProductId: firstString(raw.stripeProductId, raw.StripeProductId),
    TenantProductId: firstString(raw.tenantProductId, raw.TenantProductId),
    DisplayName: firstString(raw.displayName, raw.DisplayName, raw.productName),
    IsSubscriptionProduct: toBoolean(
      raw.isSubscriptionProduct ??
        raw.IsSubscriptionProduct ??
        raw.isSubscription
    ),
    Quantity: toOptionalNumber(raw.quantity ?? raw.Quantity) ?? 1,
    AmountMinor:
      toOptionalNumber(raw.amountMinor ?? raw.AmountMinor) ?? 0,
    ExpectedAmountMinor:
      toOptionalNumber(raw.expectedAmountMinor ?? raw.ExpectedAmountMinor) ??
      0,
    Currency: firstString(raw.currency, raw.Currency, 'CHF').toUpperCase(),
    PurchasedAtUtc: firstString(raw.purchasedAtUtc, raw.PurchasedAtUtc),
    InvoiceUrl: firstString(raw.invoiceUrl, raw.InvoiceUrl),
  };
}

function extractPurchaseRows(body: unknown): Record<string, unknown>[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  const outer = b.data ?? b.Data ?? b;

  if (Array.isArray(outer)) {
    return outer.filter(
      (x): x is Record<string, unknown> =>
        x != null && typeof x === 'object' && !Array.isArray(x)
    );
  }

  if (outer && typeof outer === 'object') {
    const p = outer as Record<string, unknown>;
    const nested = p.data ?? p.Data ?? p.items ?? p.Items;
    if (Array.isArray(nested)) {
      return nested.filter(
        (x): x is Record<string, unknown> =>
          x != null && typeof x === 'object' && !Array.isArray(x)
      );
    }
  }

  return [];
}

function readPagination(
  payload: Record<string, unknown>
): Pick<
  CustomerProductPurchasesListResponse,
  'page' | 'limit' | 'totalCount' | 'totalPageCount'
> {
  return {
    page: toOptionalNumber(payload.Page ?? payload.page) ?? 1,
    limit: toOptionalNumber(payload.Limit ?? payload.limit) ?? 20,
    totalCount: toOptionalNumber(payload.TotalCount ?? payload.totalCount) ?? 0,
    totalPageCount:
      toOptionalNumber(payload.TotalPageCount ?? payload.totalPageCount) ?? 0,
  };
}

/** GET /portal-admin/customers/{customerId}/product-purchases */
export function normalizeCustomerProductPurchasesListBody(
  body: unknown
): CustomerProductPurchasesListResponse {
  if (!body || typeof body !== 'object') {
    return {
      message: '',
      page: 1,
      limit: 20,
      totalCount: 0,
      totalPageCount: 0,
      data: [],
    };
  }

  const b = body as Record<string, unknown>;
  const message = String(b.message ?? b.Message ?? '');
  const outer = b.data ?? b.Data;

  if (outer && typeof outer === 'object' && !Array.isArray(outer)) {
    const p = outer as Record<string, unknown>;
    const rows = extractPurchaseRows(body);
    return {
      message,
      ...readPagination(p),
      data: rows.map(mapCustomerProductPurchaseFromApi),
    };
  }

  const rows = extractPurchaseRows(body);
  return {
    message,
    page: 1,
    limit: rows.length,
    totalCount: rows.length,
    totalPageCount: 1,
    data: rows.map(mapCustomerProductPurchaseFromApi),
  };
}
